import { FC, useMemo } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Heading, InputSelect, TextArea, FilePicker, NotificationBox } from '@eneco/sparky';
import { FilePickerProps } from '@eneco/sparky/types';
import { wrap } from '@sitecore/common';
import type { ContactFormRendering } from '@sitecore/types/ContactForm';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';

import type { FormFields } from '../util/types';

type Props = {
  register: UseFormRegister<FormFields>;
  fields: ContactFormRendering['fields'];
  errors: FieldErrors<FormFields>;
  selectedTopic: string;
} & Pick<FilePickerProps, 'files' | 'setFiles' | 'hasError' | 'setHasError'>;

const InquiryInputs: FC<Props> = ({
  register,
  fields,
  errors,
  selectedTopic,
  files,
  setFiles,
  hasError,
  setHasError,
}) => {
  const { inquiryContentFormField, inquiryTopicFormField, inquiryTypeFormField, hideInquiryTypeFormFieldCheckbox } =
    fields.formFields;
  const inquiryTypeOptions = inquiryTopicFormField.value.options;
  const topicNotification = useMemo(() => {
    const formAction = fields.formActions.actions.find(
      action => action.fields.data?.fieldValue.value === selectedTopic,
    );
    if (formAction) {
      const title = inquiryTopicFormField.value.options?.find(option => option.value === selectedTopic)?.label ?? '';
      return { title, text: formAction.fields.content?.messageContent?.value };
    }
    return null;
  }, [selectedTopic]);

  return (
    <>
      <Heading size="S" as="h3">
        {wrap(fields.content.inquiryTitle)}
      </Heading>
      {!hideInquiryTypeFormFieldCheckbox.value && (
        <InputSelect
          placeholder={inquiryTypeFormField.value.placeholder}
          hint={inquiryTypeFormField.value.hint}
          label={inquiryTypeFormField.value.label}
          options={inquiryTypeFormField.value.options ?? []}
          {...register('inquiryType')}
          error={errors.inquiryType?.message}
        />
      )}
      <InputSelect
        placeholder={inquiryTopicFormField.value.placeholder}
        label={inquiryTopicFormField.value.label}
        options={inquiryTypeOptions}
        hint={inquiryTopicFormField.value.hint}
        {...register('inquiryTopic')}
        error={errors.inquiryTopic?.message}
      />
      {topicNotification ? (
        <NotificationBox isAlert text={<RichText html={topicNotification.text} />} title={topicNotification.title} />
      ) : null}
      <TextArea
        label={inquiryContentFormField.value.label}
        hint={inquiryContentFormField.value.hint}
        placeholder={inquiryContentFormField.value.placeholder}
        rows={6}
        error={errors.inquiryContent?.message}
        maxLength={1000}
        {...register('inquiryContent')}
      />
      <FilePicker files={files} setFiles={setFiles} setHasError={setHasError} hasError={hasError} name="files" />
    </>
  );
};

export default InquiryInputs;
